import { useState, useEffect, useMemo, useCallback, useReducer, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonSpinner,
  IonSkeletonText,
  IonGrid,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonModal,
} from '@ionic/react';
import {
  chevronDownOutline,
  closeOutline,
  searchOutline,
  radioButtonOnOutline,
  ellipseOutline,
  checkmarkCircle,
  documentTextOutline,
  pricetagOutline,
  addCircleOutline,
} from 'ionicons/icons';
import {
  fetchZohoPricingData, fetchZohoPricelistItems, type ZohoItem, type ZohoPricelist,
} from '../services/zohoServiceOrderService';
import { getPricelistInfo } from '@/utils/pricelists';
import CreateInvoice from './CreateInvoice';
import styles from './PriceCalculator.module.scss';
import BasePageWithHeader from '../components/BasePageWithHeader';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';

// ─── Parsing — ported verbatim from PriceListDesktop.tsx, item names encode
// ─── category/frequency/type/size/condition as pipe-separated segments. ───────
const FREQ_MAP: Array<[RegExp, string]> = [
  [/bi[-\s]?weekly/i, 'Bi-Weekly'],
  [/quarterly/i,      'Quarterly'],
  [/monthly/i,        'Monthly'],
  [/weekly/i,         'Weekly'],
];

const FREQ_ORDER = ['Daily (5x/Week)', '3-4x Per Week', 'Weekly', 'Bi-Weekly', 'Monthly', 'Quarterly', 'One-Time', 'One-Time Deep Clean'];

function parseCategory(name: string): string {
  const first = name.split('|')[0].trim().toLowerCase();
  if (first.startsWith('commercial')) return 'Commercial';
  if (first.startsWith('airbnb')) return 'Airbnb';
  return i18n.t('pages.PriceCalculator.residential');
}

function parseFrequency(name: string): string {
  const cat = parseCategory(name);
  if (cat === 'Commercial') {
    const parts = name.split('|');
    return parts.length >= 3 ? parts[2].trim() : 'One-Time';
  }
  if (cat === 'Airbnb') return 'One-Time';
  const first = name.split('|')[0].trim();
  for (const [re, label] of FREQ_MAP) if (re.test(first)) return label;
  return 'One-Time';
}

function parseCleanType(name: string): string {
  const cat = parseCategory(name);
  if (cat === 'Commercial') {
    return name.split('|')[0].trim().replace(/^commercial\s*/i, '').trim();
  }
  if (cat === 'Airbnb') {
    return name.split('|')[0].trim().replace(/^airbnb\s*/i, '').trim();
  }
  let first = name.split('|')[0].trim();
  for (const [re] of FREQ_MAP) first = first.replace(re, '').replace(/\s+/g, ' ').trim();
  return first;
}

function parseCondition(name: string): string {
  const parts = name.split('|');
  return parts.length >= 3 ? parts[parts.length - 1].trim() : '';
}

function isPet(name: string): boolean {
  return name.toLowerCase().includes('pet');
}

function isAppliance(name: string): boolean {
  return name.toLowerCase().includes('appliance');
}

function isAirbnbAddon(name: string): boolean {
  return name.toLowerCase().startsWith('airbnb') && !name.includes('|');
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

// Word-by-word matching (any order, not one exact contiguous phrase) — same
// approach as CreateInvoice.tsx's item picker search. Keep '.' out of the
// strip set so "1.5 Bath" doesn't split into separate "1"/"5" tokens that a
// numeric search for "5" would then false-positive match; numeric tokens
// require an exact word match, text tokens allow a prefix match.
const normalizeSearch = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9.]/g, ' ').replace(/\s+/g, ' ').trim();

function matchesSearch(name: string, rawQuery: string): boolean {
  const tokens = normalizeSearch(rawQuery).split(' ').filter(Boolean);
  if (!tokens.length) return true;
  const hayWords = normalizeSearch(name).split(' ').filter(Boolean);
  return tokens.every(t => /^[0-9]+$/.test(t)
    ? hayWords.includes(t)
    : hayWords.some(w => w.startsWith(t)));
}

// ─── Portal dropdown — same pattern as CreateInvoice.tsx's SelectField/PickerDropdown
// ─── (itself copied from BlockedTimeForm.tsx): portals a positioned card into a fixed,
// ─── invisible root so it overlays the page instead of expanding its anchor card. ────
type PickerRect = { top: number; left?: number; right?: number; width?: number };

function usePickerDropdown(
  isOpen: boolean,
  fieldRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  close: () => void,
) {
  const [rect, setRect] = useState<PickerRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PickerRect | null => {
      const field = fieldRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      return { top: box.bottom - anchor.top + 4, left: box.left - anchor.left, width: box.width };
    };
    const applyRect = (r: PickerRect) => {
      const card = cardRef.current;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    // ion-item can still be mid-upgrade (shadow content not yet rendered) on the very
    // first paint after mounting — a rect read in this same tick can be bogus/small.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => {
      const r = computeRect();
      if (r) applyRect(r);
    };
    const handleResize = () => {
      const r = computeRect();
      if (r) { setRect(r); applyRect(r); }
    };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (fieldRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, fieldRef, portalRef, cardRef, close]);

  return rect;
}

function PickerDropdown({
  isOpen, fieldRef, portalRef, close, className, children,
}: {
  isOpen: boolean;
  fieldRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  close: () => void;
  className: string;
  children: React.ReactNode;
}) {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePickerDropdown(isOpen, fieldRef, portalRef, cardRef, close);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);
  if (!isOpen || !rect || !portalNode) return null;
  return createPortal(
    <IonCard ref={cardRef} className={className} style={{ ...rect, position: 'absolute' }}>
      {children}
    </IonCard>,
    portalNode,
  );
}

// ─── Single-select field — replaces desktop's Dropdown; mobile is a single
// ─── column throughout so there's no separate "wide" variant to track. ────────
// Loading placeholders — mirror the real cards' title / field / summary sizes so nothing jumps
// when the pricing data arrives.
const SkeletonField = ({ label }: { label: string }) => (
  <IonLabel className={styles.skeletonField}>
    <span className={styles.fieldLabel}>{label}</span>
    <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '100%', height: 42, margin: 0, borderRadius: 8 }} />
  </IonLabel>
);

// Titles are fixed text, so the skeleton shows the real title + icon (same markup as the real cards).
const SkeletonCardTitle = ({ icon, title }: { icon: string; title: string }) => (
  <IonLabel className={styles.cardTitle}>
    <IonIcon icon={icon} />{title}
  </IonLabel>
);

function SelectField({
  label, value, options, onChange, placeholder, disabled, portalRef,
}: {
  label: string;
  value: string;
  options: { value: string; label: string; description?: string }[];
  onChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
  portalRef: React.RefObject<HTMLElement | null>;
}) {
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const selected = options.find(o => o.value === value);

  return (
    <IonLabel className={styles.formGroup}>
      <span className={styles.fieldLabel}>{label}</span>
      <IonItem
        ref={fieldRef}
        button
        lines="none"
        // Passing a literal `disabled={false}` still gets reflected onto the
        // underlying custom element as a `disabled="false"` DOM attribute —
        // which a CSS `[disabled]` attribute selector matches regardless of
        // its value, dimming the field even though it's not actually disabled.
        // Passing `undefined` instead omits the attribute entirely.
        disabled={disabled || undefined}
        className={styles.selectField}
        onClick={() => setOpen(v => !v)}
      >
        <IonLabel className={!value ? styles.selectPlaceholder : undefined}>
          {selected ? translateDynamic(selected.label) : placeholder}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        className={styles.calendarDropdown}>
        <IonList lines="none" className={styles.dropdownList}>
          {options.map(o => (
            <IonItem key={o.value || '__placeholder'} button detail={false} lines="none"
              className={`${styles.dropdownItem}${o.value === value ? ` ${styles.dropdownItemActive}` : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              <IonLabel>
                <h3>{translateDynamic(o.label)}</h3>
                {o.description && <p className={styles.dropdownItemTip}>{o.description}</p>}
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      </PickerDropdown>
    </IonLabel>
  );
}

// ─── Service field — the resolved-matches list lives inside a dropdown like every
// ─── other field here, instead of an always-expanded list stacking under the form
// ─── (which visually collided with whichever picker was open above it). ──────────
function ServiceSelectField({
  services, value, onChange, portalRef, placeholder,
}: {
  services: ZohoItem[];
  value: string | null;
  onChange: (id: string) => void;
  portalRef: React.RefObject<HTMLElement | null>;
  placeholder: string;
}) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => { setOpen(false); setSearch(''); }, []);
  const selected = services.find(s => s.item_id === value);
  const filtered = services.filter(s => matchesSearch(s.name, search));

  return (
    <>
      <IonItem ref={fieldRef} button detail={false} lines="none" className={styles.selectField} onClick={() => setOpen(v => !v)}>
        <IonLabel className={!selected ? styles.selectPlaceholder : undefined}>
          {selected ? selected.name : placeholder}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        className={styles.calendarDropdown}>
        <IonItem lines="none" className={styles.searchBox}>
          <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
          <IonInput
            className={styles.searchInput}
            value={search}
            onIonInput={e => setSearch(e.detail.value ?? '')}
            placeholder={tr('pages.PriceCalculator.search')}
          />
        </IonItem>
        <IonList lines="none" className={styles.resultList}>
          {filtered.length === 0 ? (
            <IonLabel className={styles.searchEmpty}>{tr('pages.PriceCalculator.noMatches')}</IonLabel>
          ) : filtered.map(svc => (
            <IonItem key={svc.item_id} button detail={false} lines="none"
              className={`${styles.serviceOption}${value === svc.item_id ? ` ${styles.serviceOptionSelected}` : ''}`}
              onClick={() => { onChange(svc.item_id); setOpen(false); setSearch(''); }}>
              <IonIcon slot="start" icon={value === svc.item_id ? radioButtonOnOutline : ellipseOutline}
                className={styles.serviceRadioIcon} />
              <IonLabel className="ion-text-wrap">
                <h3 className={styles.serviceOptionName}>{svc.name}</h3>
              </IonLabel>
              <span className={styles.serviceOptionRate}>{fmt(svc.rate)}</span>
            </IonItem>
          ))}
        </IonList>
      </PickerDropdown>
    </>
  );
}

// ─── Multi-select field — replaces desktop's MultiSelect (Pets/Appliances/Airbnb
// ─── add-ons); a checkable option list plus a row of removable chips below. ───
function MultiSelectField({
  label, placeholder, items, selected, onToggle, portalRef,
}: {
  label: string;
  placeholder: string;
  items: ZohoItem[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  portalRef: React.RefObject<HTMLElement | null>;
}) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => { setOpen(false); setSearch(''); }, []);
  const filtered = items.filter(i => matchesSearch(i.name, search));
  const selectedItems = items.filter(i => selected.has(i.item_id));

  return (
    <IonLabel className={styles.formGroup}>
      <span className={styles.fieldLabel}>{label}</span>
      <IonItem ref={fieldRef} button detail={false} lines="none" className={styles.selectField} onClick={() => setOpen(v => !v)}>
        <IonLabel className={selected.size === 0 ? styles.selectPlaceholder : undefined}>
          {selected.size > 0 ? `${selected.size} selected` : placeholder}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        className={styles.calendarDropdown}>
        <IonItem lines="none" className={styles.searchBox}>
          <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
          <IonInput
            className={styles.searchInput}
            value={search}
            onIonInput={e => setSearch(e.detail.value ?? '')}
            placeholder={tr('pages.PriceCalculator.search')}
          />
        </IonItem>
        <IonList lines="none" className={styles.dropdownList}>
          {filtered.length === 0 ? (
            <IonLabel className={styles.searchEmpty}>{tr('pages.PriceCalculator.noMatches')}</IonLabel>
          ) : filtered.map(item => (
            <IonItem key={item.item_id} button detail={false} lines="none"
              className={`${styles.dropdownItem} ${styles.multiSelectItem}${selected.has(item.item_id) ? ` ${styles.dropdownItemActive}` : ''}`}
              onClick={() => { onToggle(item.item_id); setOpen(false); setSearch(''); }}>
              <IonIcon icon={selected.has(item.item_id) ? checkmarkCircle : ellipseOutline} slot="start"
                className={styles.multiSelectCheck} />
              <IonLabel className="ion-text-wrap"><h3>{item.name}</h3></IonLabel>
              <span className={styles.multiSelectRate}>{fmt(item.rate)}</span>
            </IonItem>
          ))}
        </IonList>
      </PickerDropdown>
      {selectedItems.length > 0 && (
        <IonLabel className={styles.chips}>
          {selectedItems.map(item => (
            <IonLabel key={item.item_id} className={styles.chip}>
              <span>{item.name}</span>
              <IonButton fill="clear" className={styles.chipRemove} onClick={() => onToggle(item.item_id)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonLabel>
          ))}
        </IonLabel>
      )}
    </IonLabel>
  );
}

// ─── Pricing-data load lifecycle ────────────────────────────────────────────────
// A reducer (rather than separate useState calls) so loadInitial's updates go
// through `dispatch` instead of individual setState setters — an effect calling
// a function that dispatches a reducer action isn't flagged by the "don't call
// a setState-containing function from an effect" rule the way an effect calling
// a function that calls setState directly is, since dispatch isn't itself a
// setState call. See PriceListDesktop.tsx for the same pattern.

interface LoadState {
  allItems: ZohoItem[];
  pricelists: ZohoPricelist[];
  loading: boolean;
  error: string | null;
  loadingZone: boolean;
}

type LoadAction =
  | { type: 'load-start' }
  | { type: 'load-success'; items: ZohoItem[]; pricelists: ZohoPricelist[] }
  | { type: 'load-error'; message: string }
  | { type: 'set-all-items'; items: ZohoItem[] }
  | { type: 'zone-load-start' }
  | { type: 'zone-load-done'; items: ZohoItem[] };

function loadReducer(state: LoadState, action: LoadAction): LoadState {
  switch (action.type) {
    case 'load-start':
      return { ...state, loading: true, error: null };
    case 'load-success':
      return { ...state, loading: false, error: null, allItems: action.items, pricelists: action.pricelists };
    case 'load-error':
      return { ...state, loading: false, error: action.message };
    case 'set-all-items':
      return { ...state, allItems: action.items };
    case 'zone-load-start':
      return { ...state, loadingZone: true };
    case 'zone-load-done':
      return { ...state, loadingZone: false, allItems: action.items };
  }
}

// ─── Main component ────────────────────────────────────────────────────────────
interface PriceCalculatorProps {
  onClose?: () => void;
}

const PriceCalculator: React.FC<PriceCalculatorProps> = ({ onClose }) => {
  const { t: tr } = useTranslation();
  const baseItems = useRef<ZohoItem[]>([]);
  const pickerPortalRef = useRef<HTMLDivElement>(null);

  const [loadState, dispatchLoad] = useReducer(loadReducer, {
    allItems: [] as ZohoItem[],
    pricelists: [] as ZohoPricelist[],
    loading: true,
    error: null as string | null,
    loadingZone: false,
  });
  const { allItems, pricelists, loading, error, loadingZone } = loadState;
  const [selectedZone, setSelectedZone]          = useState('');
  const [selectedCategory, setSelectedCategory]  = useState('');
  const [frequency, setFrequency]                = useState('');
  const [cleanType, setCleanType]                = useState('');
  const [condition, setCondition]                = useState('');
  const [selectedPets, setSelectedPets]          = useState<Set<string>>(new Set());
  const [selectedAppl, setSelectedAppl]          = useState<Set<string>>(new Set());
  const [selectedAirbnbAddons, setSelectedAirbnbAddons] = useState<Set<string>>(new Set());
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [showCreateQuote, setShowCreateQuote]    = useState(false);

  const loadInitial = useCallback(async () => {
    dispatchLoad({ type: 'load-start' });
    try {
      const data = await fetchZohoPricingData();
      baseItems.current = data.items;
      dispatchLoad({ type: 'load-success', items: data.items, pricelists: data.pricelists });
    } catch {
      dispatchLoad({ type: 'load-error', message: 'Failed to load pricing data.' });
    }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  useEffect(() => {
    if (!selectedZone) { dispatchLoad({ type: 'set-all-items', items: baseItems.current }); return; }
    dispatchLoad({ type: 'zone-load-start' });
    fetchZohoPricelistItems(selectedZone)
      .then(items => dispatchLoad({ type: 'zone-load-done', items: items.length ? items : baseItems.current }))
      .catch(() => dispatchLoad({ type: 'zone-load-done', items: baseItems.current }));
  }, [selectedZone]);


  // ── Derived item groups ───────────────────────────────────────────────────
  const serviceItems = useMemo(() =>
    allItems.filter(i =>
      (i.name.includes('|') || (parseCategory(i.name) === 'Airbnb' && !isAirbnbAddon(i.name))) &&
      !isPet(i.name) && !isAppliance(i.name) && !isAirbnbAddon(i.name)
    )
  , [allItems]);

  const filteredByCategory = useMemo(() =>
    selectedCategory ? serviceItems.filter(i => parseCategory(i.name) === selectedCategory) : serviceItems
  , [serviceItems, selectedCategory]);

  // Cascade resets — field order here is Category → Type of Cleaning → Frequency →
  // Condition, then a single "Service" dropdown (search + radio list, styled like the
  // Pets/Appliances multi-selects) lists every remaining match — size lives in each
  // match's own name/label rather than being a separate narrowing field. Applied
  // directly in each dropdown's onChange (below) rather than as effects reacting
  // to the state after the fact, per https://react.dev/learn/you-might-not-need-an-effect.
  const handleSelectCategory = useCallback((v: string) => {
    setSelectedCategory(v);
    setFrequency(v === 'Airbnb' ? 'One-Time' : '');
    setCleanType(v === 'Airbnb' ? 'Turnover Clean' : '');
    setCondition('');
    setSelectedServiceId(null);
  }, []);
  const handleSelectCleanType = useCallback((v: string) => {
    setCleanType(v);
    if (selectedCategory === 'Airbnb') {
      setCondition('');
      setSelectedServiceId(null);
      return;
    }
    // Every Type of Cleaning is one-time except "Recurring" — default straight to
    // "One-Time" whenever it's an available option for the newly-picked type,
    // instead of leaving Frequency blank for the user to confirm a foregone
    // choice. Recomputes the same set `frequencies` (below) derives, for the
    // new cleanType value directly, since that memo hasn't re-run yet at the
    // moment of this click.
    const newFrequencies = new Set<string>();
    filteredByCategory
      .filter(i => !v || parseCleanType(i.name) === v)
      .forEach(i => newFrequencies.add(parseFrequency(i.name)));
    const oneTimeDefault = v && v !== 'Recurring' && newFrequencies.has('One-Time') ? 'One-Time' : '';
    setFrequency(oneTimeDefault);
    setCondition('');
    setSelectedServiceId(null);
  }, [selectedCategory, filteredByCategory]);
  const handleSelectFrequency = useCallback((v: string) => {
    setFrequency(v);
    setCondition('');
    setSelectedServiceId(null);
  }, []);
  const handleSelectCondition = useCallback((v: string) => {
    setCondition(v);
    setSelectedServiceId(null);
  }, []);

  const petItems = useMemo(() => allItems.filter(i => isPet(i.name)), [allItems]);
  const applianceItems = useMemo(() => allItems.filter(i => isAppliance(i.name) && !isPet(i.name)), [allItems]);
  const airbnbAddonItems = useMemo(() => allItems.filter(i => isAirbnbAddon(i.name)), [allItems]);

  // Options for each field are filtered by everything upstream of it in the
  // Category → Type → Frequency → Condition → Size field order.
  const cleanTypes = useMemo(() => {
    const set = new Set<string>();
    filteredByCategory
      // "Deep Clean" is a redundant/duplicate catalog label alongside "Deep Cleaning" —
      // drop it so it's never offered as a selectable type.
      .forEach(i => { const t = parseCleanType(i.name); if (t && t !== 'Deep Clean') set.add(t); });
    return Array.from(set).sort();
  }, [filteredByCategory]);

  const frequencies = useMemo(() => {
    const set = new Set<string>();
    filteredByCategory
      .filter(i => !cleanType || parseCleanType(i.name) === cleanType)
      .forEach(i => set.add(parseFrequency(i.name)));
    return Array.from(set).sort((a, b) => {
      const ai = FREQ_ORDER.indexOf(a), bi = FREQ_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [filteredByCategory, cleanType]);

  const availableConditions = useMemo(() => {
    const set = new Set<string>();
    filteredByCategory
      .filter(i => {
        if (cleanType && parseCleanType(i.name) !== cleanType) return false;
        if (frequency && parseFrequency(i.name) !== frequency) return false;
        return true;
      })
      .forEach(i => { const c = parseCondition(i.name); if (c) set.add(c); });
    return Array.from(set).sort();
  }, [filteredByCategory, cleanType, frequency]);

  const resolvedServices = useMemo(() => {
    if (!selectedCategory && !frequency && !cleanType) return [];
    return filteredByCategory.filter(item => {
      if (frequency && parseFrequency(item.name) !== frequency) return false;
      if (cleanType && parseCleanType(item.name) !== cleanType) return false;
      if (condition && parseCondition(item.name) !== condition) return false;
      return true;
    });
  }, [filteredByCategory, selectedCategory, frequency, cleanType, condition]);

  // Auto-select when exactly 1 match — computed during render (React's
  // "adjust state when a dependency changes" pattern) instead of as an
  // effect, since this has no external system to synchronize with: it's a
  // pure function of resolvedServices, which is itself already a memo.
  const [prevResolvedServices, setPrevResolvedServices] = useState(resolvedServices);
  if (resolvedServices !== prevResolvedServices) {
    setPrevResolvedServices(resolvedServices);
    if (resolvedServices.length === 1) {
      setSelectedServiceId(resolvedServices[0].item_id);
    } else if (!resolvedServices.find(i => i.item_id === selectedServiceId)) {
      setSelectedServiceId(null);
    }
  }

  const selectedService = useMemo(() =>
    allItems.find(i => i.item_id === selectedServiceId) ?? null
  , [allItems, selectedServiceId]);

  const { total, addOnCount } = useMemo(() => {
    let t = selectedService?.rate ?? 0;
    let count = 0;
    petItems.forEach(i => { if (selectedPets.has(i.item_id)) { t += i.rate; count++; } });
    applianceItems.forEach(i => { if (selectedAppl.has(i.item_id)) { t += i.rate; count++; } });
    airbnbAddonItems.forEach(i => { if (selectedAirbnbAddons.has(i.item_id)) { t += i.rate; count++; } });
    return { total: t, addOnCount: count };
  }, [selectedService, petItems, selectedPets, applianceItems, selectedAppl, airbnbAddonItems, selectedAirbnbAddons]);

  const togglePet = useCallback((id: string) => setSelectedPets(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);
  const toggleAppl = useCallback((id: string) => setSelectedAppl(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);
  const toggleAirbnbAddon = useCallback((id: string) => setSelectedAirbnbAddons(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const clearAll = useCallback(() => {
    setSelectedZone('');
    setSelectedCategory('');
    setFrequency('');
    setCleanType('');
    setCondition('');
    setSelectedPets(new Set());
    setSelectedAppl(new Set());
    setSelectedAirbnbAddons(new Set());
    setSelectedServiceId(null);
  }, []);

  const estimateLineItems = useMemo(() => {
    const items: Array<{ description: string; quantity: number; price: number }> = [];
    if (selectedService) items.push({ description: selectedService.name, quantity: 1, price: selectedService.rate });
    petItems.filter(i => selectedPets.has(i.item_id)).forEach(i => items.push({ description: i.name, quantity: 1, price: i.rate }));
    applianceItems.filter(i => selectedAppl.has(i.item_id)).forEach(i => items.push({ description: i.name, quantity: 1, price: i.rate }));
    airbnbAddonItems.filter(i => selectedAirbnbAddons.has(i.item_id)).forEach(i => items.push({ description: i.name, quantity: 1, price: i.rate }));
    return items;
  }, [selectedService, petItems, selectedPets, applianceItems, selectedAppl, airbnbAddonItems, selectedAirbnbAddons]);

  const zoneName = selectedZone ? pricelists.find(p => p.pricelist_id === selectedZone)?.name ?? 'Custom' : '';
  const hasActiveFilters = !!(selectedZone || selectedCategory || frequency || cleanType ||
    selectedPets.size > 0 || selectedAppl.size > 0 || selectedAirbnbAddons.size > 0);
  const hasSelection = !!selectedService || selectedPets.size > 0 || selectedAppl.size > 0 || selectedAirbnbAddons.size > 0;

  const zoneOptions = [
    { value: '', label: tr('pages.PriceCalculator.baseCatalog') },
    ...pricelists.map(p => {
      const { name, tip } = getPricelistInfo(p.name);
      return { value: p.pricelist_id, label: name, description: tip };
    }),
  ];
  const categoryOptions = [
    { value: '', label: tr('pages.PriceCalculator.selectCategory') },
    ...['Residential', 'Commercial', 'Airbnb'].map(c => ({ value: c, label: c })),
  ];
  const toOptions = (list: string[], placeholder: string) => [
    { value: '', label: placeholder },
    ...list.map(v => ({ value: v, label: v })),
  ];

  const pageContent = (
    <>
      <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
      <BasePageWithHeader
        title={tr('pages.PriceCalculator.priceCalculator')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          hasActiveFilters && (
            <IonButton fill="clear" className={styles.clearAllBtn} onClick={clearAll}>{tr('pages.PriceCalculator.clearAll')}</IonButton>
          )
        }
      >
        <IonContent scrollY={false} className={styles.mainContent}>
          <IonGrid className={styles.innerGrid}>
            {loading ? (
              <IonLabel aria-hidden="true">
                <IonCard className={`${styles.card} ${styles.skeletonCard}`}>
                  <IonCardContent className={styles.cardBody}>
                    <SkeletonCardTitle icon={pricetagOutline} title={tr('pages.PriceCalculator.service')} />
                    <IonLabel className={styles.skeletonFields}>
                      <SkeletonField label={tr('pages.PriceCalculator.zonePricelist')} />
                      <SkeletonField label={tr('pages.PriceCalculator.category')} />
                      <SkeletonField label={tr('pages.PriceCalculator.typeOfCleaning')} />
                      <SkeletonField label={tr('pages.PriceCalculator.frequency')} />
                      <SkeletonField label={tr('pages.PriceCalculator.condition')} />
                      <SkeletonField label={tr('pages.PriceCalculator.size')} />
                    </IonLabel>
                  </IonCardContent>
                </IonCard>

                <IonCard className={`${styles.card} ${styles.skeletonCard}`}>
                  <IonCardContent className={styles.cardBody}>
                    <SkeletonCardTitle icon={addCircleOutline} title={tr('pages.PriceCalculator.addOns')} />
                    <IonLabel className={styles.skeletonFields}>
                      <SkeletonField label={tr('pages.PriceCalculator.pets')} />
                      <SkeletonField label={tr('pages.PriceCalculator.appliances')} />
                    </IonLabel>
                  </IonCardContent>
                </IonCard>

                <IonCard className={`${styles.card} ${styles.skeletonCard}`}>
                  <IonCardContent className={styles.cardBody}>
                    <SkeletonCardTitle icon={documentTextOutline} title={tr('pages.PriceCalculator.summary')} />
                    <IonLabel className={styles.contextBlock}>
                      {['Zone', 'Category', 'Frequency', 'Type', 'Condition'].map(lbl => (
                        <IonLabel key={lbl} className={styles.contextRow}>
                          <span className={styles.contextLabel}>{lbl}</span>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '30%', height: 13, margin: '1px 0' }} />
                        </IonLabel>
                      ))}
                    </IonLabel>
                    <IonLabel className={styles.divider} />
                    <IonLabel className={styles.summaryLineItems}>
                      <span className={styles.summaryEmpty}>{tr('pages.PriceCalculator.nothingSelectedYet')}</span>
                    </IonLabel>
                    <IonLabel className={styles.divider} />
                    <IonItem lines="none" className={styles.totalRow}>
                      <IonButton className={styles.createQuoteBtn} disabled>{tr('pages.PriceCalculator.createQuote')}</IonButton>
                      <IonLabel slot="end" className={styles.totalLabelWrap}>
                        <p className={styles.totalLabel}>{tr('pages.PriceCalculator.total')}</p>
                        <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 64, height: 18, margin: '1px 0 0 auto' }} />
                      </IonLabel>
                    </IonItem>
                  </IonCardContent>
                </IonCard>
              </IonLabel>
            ) : error ? (
              <IonLabel className={styles.centeredState}>
                <span className={styles.errorText}>{error}</span>
                <IonButton size="small" onClick={loadInitial}>{tr('pages.PriceCalculator.retry')}</IonButton>
              </IonLabel>
            ) : (
              <>
                {loadingZone && (
                  <IonLabel className={styles.loadingRow}>
                    <IonSpinner name="dots" />{tr('pages.PriceCalculator.loadingZonePrices')}
                  </IonLabel>
                )}

                {/* ── Service card ─────────────────────────────────────── */}
                <IonCard className={styles.card}>
                  <IonCardContent className={styles.cardBody}>
                    <IonLabel className={styles.cardTitle}>
                      <IonIcon icon={pricetagOutline} />{tr('pages.PriceCalculator.service')}
                    </IonLabel>
                    <IonLabel className={styles.formGrid}>
                      <SelectField label={tr('pages.PriceCalculator.zonePricelist')} value={selectedZone} onChange={setSelectedZone}
                        options={zoneOptions} placeholder={tr('pages.PriceCalculator.baseCatalog')} portalRef={pickerPortalRef} />
                      <SelectField label={tr('pages.PriceCalculator.category')} value={selectedCategory} onChange={handleSelectCategory}
                        options={categoryOptions} placeholder={tr('pages.PriceCalculator.selectCategory')} portalRef={pickerPortalRef} />
                      <SelectField label={tr('pages.PriceCalculator.typeOfCleaning')} value={cleanType} onChange={handleSelectCleanType}
                        options={toOptions(cleanTypes, tr('pages.PriceCalculator.selectType'))} placeholder={tr('pages.PriceCalculator.selectType')}
                        disabled={selectedCategory === 'Airbnb'} portalRef={pickerPortalRef} />
                      <SelectField label={tr('pages.PriceCalculator.frequency')} value={frequency} onChange={handleSelectFrequency}
                        options={toOptions(frequencies, tr('pages.PriceCalculator.selectFrequency'))} placeholder={tr('pages.PriceCalculator.selectFrequency')}
                        disabled={selectedCategory === 'Airbnb' || frequency === 'One-Time'} portalRef={pickerPortalRef} />
                      <SelectField label={tr('pages.PriceCalculator.condition')} value={condition} onChange={handleSelectCondition}
                        options={toOptions(availableConditions, tr('pages.PriceCalculator.selectCondition'))} placeholder={tr('pages.PriceCalculator.selectCondition')}
                        disabled={selectedCategory === 'Airbnb'} portalRef={pickerPortalRef} />

                      {/* Size isn't its own narrowing field — each matching item's name already
                          carries its size, so this single dropdown (styled like Pets/Appliances:
                          search + radio list) both picks the size and resolves the final service. */}
                      <IonLabel className={styles.formGroup}>
                        <span className={styles.fieldLabel}>{tr('pages.PriceCalculator.size')}</span>
                        {!cleanType ? (
                          <IonLabel className={styles.resultPrompt}>{tr('pages.PriceCalculator.select')} <strong>{tr('pages.PriceCalculator.typeOfCleaning')}</strong>{tr('pages.PriceCalculator.toSeeMatchingServices')}
                          </IonLabel>
                        ) : (
                          <ServiceSelectField services={resolvedServices}
                            value={selectedServiceId} onChange={setSelectedServiceId} portalRef={pickerPortalRef}
                            placeholder={resolvedServices.length === 0 ? tr('pages.PriceCalculator.noServicesMatchTheseFilters') : tr('pages.PriceCalculator.selectSize')} />
                        )}
                      </IonLabel>
                    </IonLabel>
                  </IonCardContent>
                </IonCard>

                {/* ── Add-ons card ─────────────────────────────────────── */}
                <IonCard className={styles.card}>
                  <IonCardContent className={styles.cardBody}>
                    <IonLabel className={styles.cardTitle}>
                      <IonIcon icon={addCircleOutline} />{tr('pages.PriceCalculator.addOns')}
                    </IonLabel>
                    <IonLabel className={styles.formGrid}>
                      <MultiSelectField label={tr('pages.PriceCalculator.pets')} placeholder={tr('pages.PriceCalculator.selectPetOptions')}
                        items={petItems} selected={selectedPets} onToggle={togglePet} portalRef={pickerPortalRef} />
                      <MultiSelectField label={tr('pages.PriceCalculator.appliances')} placeholder={tr('pages.PriceCalculator.selectAppliances')}
                        items={applianceItems} selected={selectedAppl} onToggle={toggleAppl} portalRef={pickerPortalRef} />
                      {selectedCategory === 'Airbnb' && (
                        <MultiSelectField label={tr('pages.PriceCalculator.airbnbAddOns')} placeholder={tr('pages.PriceCalculator.selectAirbnbAddOns')}
                          items={airbnbAddonItems} selected={selectedAirbnbAddons} onToggle={toggleAirbnbAddon}
                          portalRef={pickerPortalRef} />
                      )}
                    </IonLabel>
                  </IonCardContent>
                </IonCard>

                {/* ── Summary card ─────────────────────────────────────── */}
                <IonCard className={styles.card}>
                  <IonCardContent className={styles.cardBody}>
                    <IonLabel className={styles.cardTitle}>
                      <IonIcon icon={documentTextOutline} />{tr('pages.PriceCalculator.summary')}
                    </IonLabel>

                    <IonLabel className={styles.contextBlock}>
                      {([
                        ['Zone',      zoneName || 'Base Catalog'],
                        ['Category',  selectedCategory || '—'],
                        ['Frequency', frequency || '—'],
                        ['Type',      cleanType || '—'],
                        ['Condition', condition || '—'],
                      ] as [string, string][]).map(([lbl, val]) => (
                        <IonLabel key={lbl} className={styles.contextRow}>
                          <span className={styles.contextLabel}>{lbl}</span>
                          <span className={styles.contextValue}>{val}</span>
                        </IonLabel>
                      ))}
                    </IonLabel>

                    <IonLabel className={styles.divider} />

                    <IonLabel className={styles.summaryLineItems}>
                      {!hasSelection ? (
                        <span className={styles.summaryEmpty}>{tr('pages.PriceCalculator.nothingSelectedYet')}</span>
                      ) : (
                        <Fragment>
                          {selectedService && (
                            <IonLabel className={styles.summaryLineItem}>
                              <span className={styles.summaryLineItemName}>{selectedService.name}</span>
                              <span className={styles.summaryLineItemTotal}>{fmt(selectedService.rate)}</span>
                            </IonLabel>
                          )}
                          {petItems.filter(i => selectedPets.has(i.item_id)).map(item => (
                            <IonLabel key={item.item_id} className={styles.summaryLineItem}>
                              <span className={styles.summaryLineItemName}>{item.name}</span>
                              <span className={styles.summaryLineItemTotal}>{fmt(item.rate)}</span>
                            </IonLabel>
                          ))}
                          {applianceItems.filter(i => selectedAppl.has(i.item_id)).map(item => (
                            <IonLabel key={item.item_id} className={styles.summaryLineItem}>
                              <span className={styles.summaryLineItemName}>{item.name}</span>
                              <span className={styles.summaryLineItemTotal}>{fmt(item.rate)}</span>
                            </IonLabel>
                          ))}
                          {airbnbAddonItems.filter(i => selectedAirbnbAddons.has(i.item_id)).map(item => (
                            <IonLabel key={item.item_id} className={styles.summaryLineItem}>
                              <span className={styles.summaryLineItemName}>{item.name}</span>
                              <span className={styles.summaryLineItemTotal}>{fmt(item.rate)}</span>
                            </IonLabel>
                          ))}
                        </Fragment>
                      )}
                    </IonLabel>

                    <IonLabel className={styles.divider} />

                    <IonItem lines="none" className={styles.totalRow}>
                      <IonButton className={styles.createQuoteBtn}
                        disabled={estimateLineItems.length === 0} onClick={() => setShowCreateQuote(true)}>{tr('pages.PriceCalculator.createQuote')}
                      </IonButton>
                      <IonLabel slot="end" className={styles.totalLabelWrap}>
                        <p className={styles.totalLabel}>{tr('pages.PriceCalculator.total')}{addOnCount > 0 ? tr('pages.PriceCalculator.addOn', { addOnCount, v2: addOnCount !== 1 ? 's' : '' }) : ''}
                        </p>
                        <h2 className={`${styles.totalValue}${total > 0 ? ` ${styles.totalValueActive}` : ''}`}>{fmt(total)}</h2>
                      </IonLabel>
                    </IonItem>
                  </IonCardContent>
                </IonCard>
              </>
            )}
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>

      <IonModal
        isOpen={showCreateQuote}
        onDidDismiss={() => setShowCreateQuote(false)}
        onWillDismiss={() => setShowCreateQuote(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <CreateInvoice
          prefill={{ lineItems: estimateLineItems, invoiceType: 'QUOTE' }}
          onClose={() => setShowCreateQuote(false)}
          onCreated={() => setShowCreateQuote(false)}
        />
      </IonModal>
    </>
  );

  return onClose ? <IonLabel className={styles.modalWrapper}>{pageContent}</IonLabel> : <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default PriceCalculator;
